import './OrdersPanel.scss';
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { MdExpandLess, MdExpandMore, MdOpenInNew } from 'react-icons/md';
import { formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Badge } from '@Components/Badge';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner';
import { ModalAreYouSure, useSetAreYouSure, useSetError } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useLocalStorageState } from '../../utils/hooks/useData';
import { toPrettyDate } from '../StreamingBuying/order-view-utils';
import { useStreamingOrder } from '../StreamingBuying/use-streaming-buying';
import { agentOrderStatusLabel, needsApproval, splitAgentOrders } from './agent-orders-utils';
import { PendingAgentOrder } from './agent-plan-buy-constants';
import { approveAgentOrder, deleteAgentOrder, useAgentOrders } from './use-agent-orders';

const money = (value: string | number | null | undefined): string =>
  formatMoney(parseFloat(`${value ?? 0}`));

interface OrdersPanelProps {
  agentOrders: ReturnType<typeof useAgentOrders>;
  // Drafts the agent created in its latest reply; their rows open and are highlighted.
  newOrderIds: number[];
}

// Orders the media-planning agent drafted and has not sent, shown beside the chat. A person
// approves a draft here; then the agent can send it. A person can also delete an order to start
// over. Load errors are shown by the page.
export const OrdersPanel = ({ agentOrders, newOrderIds }: OrdersPanelProps) => {
  const { data, isLoading, refetch } = agentOrders;
  const { drafts, approved } = useMemo(() => splitAgentOrders(data ?? []), [data]);
  const [hideApproved, setHideApproved] = useLocalStorageState('agentPlanBuy.hideApproved', false);

  if (isLoading) return <Spinner />;

  if (drafts.length === 0 && approved.length === 0) {
    return (
      <div className='orders-panel'>
        <p className='orders-panel-empty'>
          The media-planning agent has no orders for this company yet.
        </p>
      </div>
    );
  }

  const renderRow = (order: PendingAgentOrder) => (
    <AgentOrderRow
      key={order.id}
      order={order}
      isNew={newOrderIds.includes(order.id)}
      onChanged={() => refetch()}
    />
  );

  return (
    <div className='orders-panel'>
      <div className='orders-panel-group'>
        <h6>Needs approval ({drafts.length})</h6>
        {drafts.length === 0 ? (
          <p className='orders-panel-empty'>No drafts to approve.</p>
        ) : (
          drafts.map(renderRow)
        )}
      </div>
      {approved.length > 0 && (
        <div className='orders-panel-group'>
          <div className='orders-panel-group-header'>
            <h6>Approved ({approved.length})</h6>
            <Button
              size={Size.SM}
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              onClick={() => setHideApproved(!hideApproved)}
            >
              {hideApproved ? 'Show' : 'Hide'}
            </Button>
          </div>
          {!hideApproved && approved.map(renderRow)}
        </div>
      )}
    </div>
  );
};

interface AgentOrderRowProps {
  order: PendingAgentOrder;
  isNew: boolean;
  onChanged: () => void;
}

const AgentOrderRow = ({ order, isNew, onChanged }: AgentOrderRowProps) => {
  const { cid: company } = useCompanyInfo();
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const [busy, setBusy] = useState<'approve' | 'delete'>();
  const [expanded, setExpanded] = useState(isNew);
  const isDraft = needsApproval(order);

  // The row can mount before the page learns it is new, so open it when that arrives.
  useEffect(() => {
    if (isNew) {
      setExpanded(true);
    }
  }, [isNew]);

  const confirmAndRun = async (
    action: 'approve' | 'delete',
    confirm: ModalAreYouSure,
    run: (id: number) => Promise<void>
  ) => {
    try {
      await setAreYouSure(confirm);
    } catch {
      return;
    }
    setBusy(action);
    try {
      await run(order.id);
      onChanged();
    } catch (e) {
      setError({ message: `Failed to ${action} order ${order.id}. ${e.message}`, reportError: e });
    } finally {
      setBusy(undefined);
    }
  };

  const approve = () =>
    confirmAndRun(
      'approve',
      {
        title: 'Approve this order?',
        message: `Approve order ${order.id} for ${order.network}, at ${money(order.spend)}. This does not send it. After you approve it, the agent can send it to the network.`,
        okayText: 'Approve'
      },
      approveAgentOrder
    );

  const remove = () =>
    confirmAndRun(
      'delete',
      {
        title: 'Delete this order?',
        message: `Delete order ${order.id} for ${order.network}, and all its flights. You cannot undo this.`,
        okayText: 'Delete'
      },
      deleteAgentOrder
    );

  return (
    <section className={cn('agent-order-row', { 'is-approved': !isDraft, 'is-new': isNew })}>
      <div className='agent-order-row-header'>
        <IconButton
          size={Size.SM}
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          aria-label={expanded ? 'Hide flights' : 'Show flights'}
          aria-expanded={expanded}
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? <MdExpandLess /> : <MdExpandMore />}
        </IconButton>
        <div className='agent-order-row-title'>
          <a
            href={`/${company}/streaming/buying/${order.network}/${order.id}`}
            target='_blank'
            rel='noreferrer'
          >
            Order {order.id}.{order.revision} <MdOpenInNew />
          </a>
          <span>{order.network}</span>
          <Badge size={Size.SM} design={isDraft ? Intent.WARNING : Intent.SUCCESS}>
            {agentOrderStatusLabel(order)}
          </Badge>
        </div>
      </div>
      <div className='agent-order-row-summary'>
        {order.start && order.end && (
          <span>
            {toPrettyDate(order.start)} - {toPrettyDate(order.end)}
          </span>
        )}
        <strong>{money(order.spend)}</strong>
        <div className='agent-order-row-actions'>
          <Button
            size={Size.SM}
            design={Intent.ERROR}
            appearance={Appearance.OUTLINED}
            loading={busy === 'delete'}
            disabled={busy === 'approve'}
            onClick={remove}
          >
            Delete
          </Button>
          {isDraft && (
            <Button
              size={Size.SM}
              loading={busy === 'approve'}
              disabled={busy === 'delete'}
              onClick={approve}
            >
              Approve
            </Button>
          )}
        </div>
      </div>
      {/* Mounted only when open, so a collapsed row does not fetch its flights. */}
      {expanded && <AgentOrderFlights orderID={`${order.id}`} />}
    </section>
  );
};

interface AgentOrderFlightsProps {
  orderID: string;
}

// The order's flights, from GET /order.
const AgentOrderFlights = ({ orderID }: AgentOrderFlightsProps) => {
  const { data, isLoading, isError } = useStreamingOrder(orderID);
  const flights = (data?.flights ?? []).filter(flight => !flight.canceled);

  if (isLoading) return <Spinner />;
  if (isError) return <p>Could not load the flights for this order.</p>;
  if (flights.length === 0) return <p>This order has no flights.</p>;

  return (
    <>
      <div className='agent-order-flights-scroll'>
        <table className='agent-order-flights'>
          <thead>
            <tr>
              <th>Flight</th>
              <th>Property</th>
              <th>Description</th>
              <th>Dates</th>
              <th>Impressions</th>
              <th>CPM</th>
              <th>Spend</th>
            </tr>
          </thead>
          <tbody>
            {flights.map(flight => (
              <tr key={flight.id}>
                <td>{flight.id}</td>
                <td>{flight.platform}</td>
                <td>{flight.description || '-'}</td>
                <td>
                  {toPrettyDate(flight.startDate)} - {toPrettyDate(flight.endDate)}
                </td>
                <td>{(flight.impressions || 0).toLocaleString('en-US')}</td>
                <td>{money(flight.cpm)}</td>
                <td>{money(flight.spend)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data?.pdfText ? (
        <p>
          <strong>Special instructions:</strong> {data.pdfText}
        </p>
      ) : undefined}
    </>
  );
};
